import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useReducer, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {AdapterField, ConnectionFieldType} from 'instruments/connections/ConnectionConstants';
import {getAdapter} from 'instruments/connections/ConnectionDAO';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import {GATEWAY_TYPE_TO_INSTRUMENT_TYPE, GatewayType} from './InstrumentGatewayConstants';
import {createGateway} from './InstrumentGatewayDAO';
import {BUCKET_ERROR, isValidBucket, isValidRoleArn, ROLE_ARN_ERROR} from './S3GatewayValidators';

export function getBucketPolicy(bucketName: string, benchlingRoleArn: string): string {
  return JSON.stringify(
    {
      Sid: 'BenchlingConnect',
      Effect: 'Allow',
      Principal: {
        AWS: benchlingRoleArn,
      },
      Action: ['s3:ListBucket', 's3:GetObject', 's3:PutObject'],
      Resource: [`arn:aws:s3:::${bucketName}`, `arn:aws:s3:::${bucketName}/*`],
      Condition: {
        StringEquals: {
          'aws:PrincipalTag/TenantId': BenchlingConfig.TENANT_ID,
        },
      },
    },
    null,
    2
  );
}

function openBucketPolicyTab(bucketName: string, benchlingRoleArn: string): void {
  const blob = new Blob([getBucketPolicy(bucketName, benchlingRoleArn)], {type: 'application/json'});
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener noreferrer');
}

interface ViewBucketInfoModalProps {
  roleArn: string;
  onRequestClose: () => void;
  gatewayName: string;
  bucketName: string;
}

export function ViewBucketInfoModal(props: ViewBucketInfoModalProps): JSX.Element {
  const {bucketName, roleArn, gatewayName, onRequestClose} = props;
  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          text: 'Generate policy',
          disabled: !bucketName,
          onClick: () => {
            openBucketPolicyTab(bucketName, roleArn);
          },
          submit: false,
        },
        {
          style: ButtonStyle.PRIMARY,
          text: 'Close',
          cancel: true,
        },
      ]}
      title="S3 Bucket information"
      onRequestClose={onRequestClose}
      closeable={true}
    >
      The following cloud gateway is connected to an AWS S3 bucket.
      <br />
      <br />
      <TextField label="Gateway name" value={gatewayName} disabled={true} onChange={() => {}} />
      <br />
      <TextField label="Bucket" value={bucketName} disabled={true} onChange={() => {}} />
      <br />
      <TextField label="Benchling role ARN" value={roleArn} disabled={true} onChange={() => {}} />
    </Modal>
  );
}

interface CreateS3GatewayModalProps {
  roleArn: string;
  onRequestClose: () => void;
}

export function CreateS3GatewayModal(props: CreateS3GatewayModalProps): JSX.Element {
  const {roleArn, onRequestClose} = props;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [gatewayName, setGatewayName] = useState('');
  const [gatewayFields, setGatewayFields] = useState<Array<AdapterField>>([]);
  const [fieldValues, updateFieldValue] = useReducer(
    (state: Record<string, string>, action: Record<string, string>) => ({...state, ...action}),
    {}
  );

  useEffect(() => {
    const instrumentType = GATEWAY_TYPE_TO_INSTRUMENT_TYPE[GatewayType.S3];
    if (!instrumentType) {
      return;
    }
    getAdapter(instrumentType, GatewayType.S3).then((adapter) => {
      const gwFields = adapter.fields.filter((field) => field.fieldType === ConnectionFieldType.GATEWAY);
      setGatewayFields(gwFields);
      const initialValues: Record<string, string> = {};
      for (const field of gwFields) {
        initialValues[field.id] = field.defaultValue || '';
      }
      updateFieldValue(initialValues);
    });
  }, []);

  const bucketName = fieldValues.customer_bucket || '';
  const customerRoleArn = fieldValues.role_arn || '';
  const allRequiredFieldsFilled =
    gatewayName && gatewayFields.filter((f) => f.isRequired).every((f) => fieldValues[f.id]?.trim());

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.PRIMARY,
          text: 'Generate policy',
          disabled: !bucketName,
          onClick: () => {
            openBucketPolicyTab(bucketName.trim(), roleArn);
          },
          submit: false,
        },
        {
          style: ButtonStyle.PRIMARY,
          text: 'Configure gateway',
          disabled: !allRequiredFieldsFilled,
          submit: true,
        },
      ]}
      title="Configure cloud gateway"
      onRequestClose={() => {
        onRequestClose();
      }}
      closeable={true}
      onRequestSubmit={async () => {
        if (!isValidBucket(bucketName.trim())) {
          setErrorMessage(BUCKET_ERROR);
          return;
        }
        if (customerRoleArn && !isValidRoleArn(customerRoleArn.trim())) {
          setErrorMessage(ROLE_ARN_ERROR);
          return;
        }
        setErrorMessage(null);
        const fields: Record<string, string> = {};
        for (const field of gatewayFields) {
          fields[field.id] = fieldValues[field.id]?.trim() || '';
        }
        await withLoadingScreen(createGateway(gatewayName, 'S3', fields), 'Setting up instrument gateway...');
        onRequestClose();
      }}
    >
      <div>
        <div>
          To connect to your S3 bucket, the following steps are required:
          <br />
          <br />
          1. Enter a Gateway Name for identification in Benchling
          <br />
          2. Provide your S3 Bucket Name
          <br />
          3. Generate and Apply the Policy to your Bucket
        </div>
        <br />
        <div className={css(styles.spacer)}>
          <TextField
            label="Gateway name"
            isRequired={true}
            value={gatewayName}
            onChange={(e) => {
              setGatewayName(e.target.value);
            }}
          />
          <br />
          {gatewayFields.map((field) => (
            <div key={field.id}>
              <TextField
                label={field.displayName}
                isRequired={field.isRequired}
                value={fieldValues[field.id] || ''}
                disabled={field.isUneditable}
                onChange={(e) => {
                  updateFieldValue({[field.id]: e.target.value});
                  setErrorMessage(null);
                }}
              />
              <br />
            </div>
          ))}
        </div>
        {errorMessage && <InlineBanner bannerType={InlineBannerType.ERROR} supportingInfo={errorMessage} />}
      </div>
    </Modal>
  );
}

export const styles = StyleSheet.create({
  spacer: {
    marginTop: '10px',
  },
});
